import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select, { components as RsComponents } from "react-select";

import NoIndexSEO from "../../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../../utils/seoConfig";
import {
  getSalaries,
  salariesDataAction,
  getHospitalStates,
} from "../../../redux/slices/salariesSlice";
import salariesService from "../../../api/salariesService";
import usePlan from "../../../hooks/usePlan";
import usePageTracking from "../../../hooks/usePageTracking";
import {
  activityEnd,
  activityStart,
} from "../../../redux/slices/trackActivitySlice";
import { sessionId } from "../../../redux/slices/cityComparisonSlice";
import { calculatorAnalytics } from "../../../redux/slices/lifeinsuranceSlice";
import { UserRound } from "lucide-react";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../../utils/profileCompletion";

import "./salaries.scss";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect the salary comparison shown here.
const SALARIES_RELEVANT_PROFILE_NUDGES = [
  {
    key: "role",
    message: "Add your nursing role to see salaries matched to your role.",
  },
  {
    key: "city",
    message: "Add your city and state to auto-fill your default state filter.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to compare against hospital rates.",
  },
];

/* ── Icons ──────────────────────────────────────────────────── */
const Icon = {
  Lock: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="4.5" y="11" width="15" height="9" rx="2" />
      <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
    </svg>
  ),
  Spark: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M12 2 13.6 8.4 20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6z" />
    </svg>
  ),
  Plus: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  Close: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Chevron: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  ChevronRight: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  ),
  Grid: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      {...p}
    >
      <rect x="3" y="3" width="8" height="8" rx="2" />
      <rect x="13" y="3" width="8" height="8" rx="2" />
      <rect x="3" y="13" width="8" height="8" rx="2" />
      <rect x="13" y="13" width="8" height="8" rx="2" />
    </svg>
  ),
  Info: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-5" />
      <path d="M12 8h.01" />
    </svg>
  ),
};

/* ── Info tooltip — small (i) icon. Self-contained (inline styles) so it
   doesn't depend on salaries.scss classes. Desktop (real mouse) hovers
   in/out; touch devices tap-to-toggle + tap-outside-to-close (hover alone
   flickers open/closed on tap — mobile browsers synthesize a
   mouseenter+mouseleave pair before the click lands). ─────────────────── */
const isHoverCapable = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Tooltip box width — clamped further per-viewport in reposition() below.
const INFO_TIP_WIDTH = 220;
const INFO_TIP_MARGIN = 12;

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const hoverCapable = useRef(isHoverCapable()).current;

  // Anchors the popup to the icon's actual screen position (fixed, not
  // relative to the wrapper) and clamps it so it never spills past the
  // left/right edge of the viewport — the old centered-under-icon
  // placement clipped off-screen for icons near a narrow column's edge.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(INFO_TIP_WIDTH, vw - INFO_TIP_MARGIN * 2);
    const iconCenter = rect.left + rect.width / 2;
    const left = Math.max(
      INFO_TIP_MARGIN,
      Math.min(iconCenter - width / 2, vw - width - INFO_TIP_MARGIN),
    );
    const arrowLeft = Math.max(10, Math.min(iconCenter - left, width - 10));
    setPos({ left, bottom: window.innerHeight - rect.top + 8, width, arrowLeft });
  };

  useLayoutEffect(() => {
    if (!show) return undefined;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [show]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={hoverCapable ? () => setShow(false) : undefined}
    >
      <Icon.Info
        style={{ width: 14, height: 14, color: "var(--fg-4)", cursor: "pointer" }}
        aria-label="More info"
        onClick={(e) => {
          e.stopPropagation();
          setShow((s) => !s);
        }}
      />
      {show &&
        pos &&
        createPortal(
          // Portaled to document.body: the Deductions tooltip lives inside
          // the Tax popup .sheet, which has `transform: translateY(...)`
          // even in its "open" state (translateY(0) still counts as a
          // transform other than none) — that makes .sheet the containing
          // block for any position:fixed descendant instead of the
          // viewport, so our getBoundingClientRect()-based left/bottom
          // values would land in the wrong place. Rendering here escapes
          // that trap. z-index bumped past .sheet's 9001 so the tooltip
          // still shows above the popup once it's a body-level sibling.
          <div
            role="tooltip"
            style={{
              position: "fixed",
              left: pos.left,
              bottom: pos.bottom,
              background: "#0f172a",
              color: "#fff",
              fontSize: 12,
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: "normal",
              lineHeight: 1.4,
              padding: "8px 10px",
              borderRadius: 8,
              width: pos.width,
              zIndex: 9999,
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            }}
          >
            {text}
            <div
              style={{
                position: "absolute",
                bottom: -4,
                left: pos.arrowLeft,
                transform: "translateX(-50%) rotate(45deg)",
                width: 8,
                height: 8,
                background: "#0f172a",
              }}
            />
          </div>,
          document.body,
        )}
    </span>
  );
};

/* ── Config ─────────────────────────────────────────────────── */
const DROPDOWN_OPTIONS = {
  rate_per: ["Hour", "Week", "Bi-Weekly", "Month", "Year"],
  hours_per_week: [4, 8, 12, 16, 20, 24, 28, 30, 32, 36, 40],
  years_of_experience: [0, 1, 2, 3, 4, 5, 10, 15, 20, 25, 30],
  position: [
    "Base",
    "Evening Shift",
    "Night Shift",
    "Weekend",
    "Weekend Evenings",
    "Weekend Nights",
    "Per Diem Days",
    "Per Diem Evenings",
    "Per Diem Nights",
  ],
  over_time: [0, 4, 8, 12, 16, 20, 24, 28, 30, 32, 36, 40],
  double_time: [0, 4, 8, 12, 16, 20, 24, 28, 30, 32, 36, 40],
};

const OPEN_INPUTS = [
  { key: "rate_per", label: "Rate Per" },
  { key: "hours_per_week", label: "Hours Per Week", unit: "hrs" },
  { key: "years_of_experience", label: "Years of Experience", unit: "yrs" },
];

const PRO_INPUTS = [
  {
    key: "position",
    label: "Position",
    lockedValue: "Base",
    paywall: "Adjust shift differentials with Pro",
  },
  {
    key: "over_time",
    label: "Over Time hours",
    lockedValue: "0",
    paywall: "Add overtime hours with Pro",
  },
  {
    key: "double_time",
    label: "Double Time hours",
    lockedValue: "0",
    paywall: "Add double time hours with Pro",
  },
];

const DEDUCTIONS = [
  { label: "Federal", key: "federalTax" },
  { label: "State (CA)", key: "stateTax" },
  { label: "Social Security", key: "ssTax" },
  { label: "Medicare", key: "medicareTax" },
  { label: "SDI", key: "sdiTax" },
];

// Map filters.rate_per → human-friendly suffix shown next to "Gross".
const RATE_PER_LABEL = {
  Hour: "hourly",
  Week: "weekly",
  "Bi-Weekly": "bi-weekly",
  Month: "monthly",
  Year: "annual",
};

// Short suffix appended to salary amounts in the rankings list ($14,820/mo).
const RATE_PER_SHORT = {
  Hour: "/hr",
  Week: "/wk",
  "Bi-Weekly": "/bw",
  Month: "/mo",
  Year: "/yr",
};

// 30 (not 10): a same-pay group (e.g. the 11 NYC H+H hospitals, or a future
// 20+ hospital Kaiser breakout) always arrives complete in whichever page
// it's on — the backend paginates by ranking slot, embedding a group's full
// member list regardless of `length` (see hospitals_salary.js
// findAllWithPaginationGrouped) — so this is purely "fewer scroll fetches,"
// not a correctness requirement.
const DEFAULT_FILTERS = {
  page_type: "salary",
  page: 1,
  length: 10,
  rate_per: "Month",
  hours_per_week: 40,
  years_of_experience: 5,
  position: "Base",
  over_time: 0,
  double_time: 0,
  state: "",
  deductions: {},
};

const LOCKED_AMOUNT = "$•,•••.••";
const LOCKED_SUMMARY = "$•••,•••";

// Group cards are a Pro-tier surface. Members shown inline before an
// expanded group falls back to a "…and N more" row.
const GROUP_MEMBERS_VISIBLE_CAP = 6;

// Each entry in `salariesData` is already a finished ranking slot from the
// backend (hospitals_salary.js `findAllWithPaginationGrouped`): either a
// plain hospital doc, or `{ group: { id, name, tag, size, hospitals: [...] } }`
// with every member of that same-pay group embedded and complete — the
// backend paginates over slots, not raw hospital rows, so a group's member
// list is never partial/split across a page the way it would be if we
// paginated hospitals directly. This just reshapes each slot for rendering.
//
// Grouping itself is Pro-only: free users never see a collapsed group card
// — a group's members are unpacked back into individual rows instead, same
// as if the backend hadn't grouped them at all.
const buildRankedEntries = (data, isProUser) =>
  data.flatMap((doc, i) => {
    if (doc.group) {
      const members = [...doc.group.hospitals].sort((a, b) =>
        (a.hospital || "").localeCompare(b.hospital || ""),
      );
      if (!isProUser) {
        return members.map((h) => ({
          type: "single",
          key: `single-${h.id ?? h.hospital}`,
          hospital: h,
        }));
      }
      // No known system name (e.g. not yet in hospital_pay_groups.js) →
      // title the card with a real member hospital's name instead of a
      // generic placeholder; the "Same-pay group" badge already signals
      // it's a collapsed group, so the title doesn't need to repeat that.
      const fallbackName = (members[0]?.hospital || "").trim();
      return [
        {
          type: "group",
          key: doc.group.id,
          name: doc.group.name || fallbackName || "Multiple hospitals",
          tag: doc.group.tag,
          size: doc.group.size,
          amount: members[0]?.grossPay,
          members,
        },
      ];
    }
    return [
      {
        type: "single",
        key: `single-${doc.id ?? doc.hospital ?? i}`,
        hospital: doc,
      },
    ];
  });

/* ── Helpers ────────────────────────────────────────────────── */
const formatCurrency = (v) =>
  v == null || isNaN(v)
    ? "$0.00"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(v);

// Enter/Space → activate. Reused by every clickable non-button row.
const onActivate = (fn) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn?.();
  }
};

/* Run layout work synchronously before paint when possible. Falls back to
 * useEffect during SSR so React doesn't warn about a missing DOM. */
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/* Swipe-down to dismiss bottom sheets on touch devices. Attaches touch
 * listeners to the returned ref and translates the sheet with the finger;
 * if the user drags past the threshold (or flicks down) it calls onClose.
 * touchmove is non-passive so we can preventDefault on downward drags and
 * stop the page underneath from scrolling/rubber-banding on iOS. */
const useSwipeDismiss = (open, onClose) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !open) return undefined;

    let startY = 0;
    let lastY = 0;
    let startTime = 0;
    let dragging = false;
    let moved = false;

    const reset = () => {
      el.style.transition = "";
      el.style.transform = "";
      dragging = false;
      moved = false;
    };

    const onTouchStart = (e) => {
      const t = e.touches[0];
      startY = t.clientY;
      lastY = t.clientY;
      startTime = Date.now();
      dragging = true;
      moved = false;
    };

    const onTouchMove = (e) => {
      if (!dragging) return;
      const t = e.touches[0];
      const delta = t.clientY - startY;
      lastY = t.clientY;
      if (Math.abs(delta) > 6) moved = true;
      if (delta > 0) {
        // Stop the page beneath from scrolling/rubber-banding while we drag
        // the sheet. Requires the listener to be registered passive:false.
        if (e.cancelable) e.preventDefault();
        el.style.transition = "none";
        el.style.transform = `translateY(${delta}px)`;
      }
    };

    const onTouchEnd = () => {
      if (!dragging) {
        reset();
        return;
      }
      const delta = lastY - startY;
      const elapsed = Date.now() - startTime;
      const velocity = delta / Math.max(elapsed, 1);
      const shouldClose =
        moved && (delta > 100 || (delta > 40 && velocity > 0.6));
      dragging = false;
      moved = false;

      if (shouldClose) {
        el.style.transition = "transform 220ms cubic-bezier(0.2,0.8,0.2,1)";
        el.style.transform = "translateY(110%)";
        window.setTimeout(() => {
          el.style.transition = "";
          el.style.transform = "";
          onClose?.();
        }, 200);
      } else {
        el.style.transition = "";
        el.style.transform = "";
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      reset();
    };
  }, [open, onClose]);

  return ref;
};

// Load saved filters; for free users force pro-only fields to safe defaults
// so the backend calc reflects what the locked UI shows.
const loadInitialFilters = (isPro) => {
  let merged = { ...DEFAULT_FILTERS };
  try {
    const saved = JSON.parse(localStorage.getItem("filters"));
    if (saved) merged = { ...merged, ...saved, page: 1 };
  } catch (e) {
    /* corrupted localStorage — fall through to defaults */
  }
  if (!isPro) {
    merged.position = "Base";
    merged.over_time = 0;
    merged.double_time = 0;
  }
  return merged;
};

/* ── react-select styling ───────────────────────────────────── */
// The menu is portaled to document.body, outside .mmp-locked-salary's
// scope, so its page-local --brand-*/--fg-* tokens wouldn't resolve there.
// The global --mmp-* tokens (defined on :root / [data-theme]) do reach the
// portal though, so route through those instead of literal hex.
const T = {
  brandInk: "var(--mmp-text-primary)",
  brand500: "var(--mmp-accent)",
  brand100: "var(--mmp-accent-border)",
  brand50: "var(--mmp-accent-bg)",
  border: "var(--mmp-border)",
  borderStrong: "var(--mmp-border)",
  fg2: "var(--mmp-text-primary)",
  fg3: "var(--mmp-text-secondary)",
  fg4: "var(--mmp-text-secondary)",
};

const selectStyles = {
  control: (b, s) => ({
    ...b,
    background: "var(--mmp-surface)",
    border: `1px solid ${s.isFocused ? T.brand500 : T.border}`,
    borderRadius: 10,
    minHeight: 42,
    boxShadow: s.isFocused ? "0 0 0 3px rgba(3,149,211,0.15)" : "none",
    paddingLeft: 12,
    paddingRight: 4,
    cursor: "pointer",
    transition: "border-color 150ms, box-shadow 150ms",
    "&:hover": { borderColor: s.isFocused ? T.brand500 : T.borderStrong },
  }),
  valueContainer: (b) => ({
    ...b,
    padding: 0,
    justifyContent: "flex-end",
    flexWrap: "nowrap",
  }),
  singleValue: (b) => ({
    ...b,
    fontSize: 13,
    fontWeight: 800,
    color: T.brandInk,
    fontVariantNumeric: "tabular-nums",
    margin: 0,
    textAlign: "right",
  }),
  input: (b) => ({ ...b, margin: 0, padding: 0, color: "transparent" }),
  placeholder: (b) => ({ ...b, color: T.fg4, margin: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b, s) => ({
    ...b,
    color: T.fg3,
    padding: "0 6px 0 4px",
    transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 180ms cubic-bezier(0.2,0.8,0.2,1)",
    svg: { width: 16, height: 16 },
  }),
  menu: (b) => ({
    ...b,
    background: "var(--mmp-surface)",
    borderRadius: 12,
    marginTop: 6,
    padding: 6,
    boxShadow: "0 12px 32px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
    overflow: "hidden",
    zIndex: 1000,
  }),
  menuPortal: (b) => ({ ...b, zIndex: 9500 }),
  menuList: (b) => ({ ...b, padding: 0, maxHeight: 240 }),
  option: (b, s) => ({
    ...b,
    fontSize: 13,
    fontWeight: s.isSelected ? 800 : 600,
    fontVariantNumeric: "tabular-nums",
    borderRadius: 8,
    padding: "10px 12px",
    margin: "2px 0",
    cursor: "pointer",
    color: s.isSelected ? "#fff" : T.brandInk,
    background: s.isSelected
      ? T.brand500
      : s.isFocused
      ? T.brand50
      : "transparent",
    transition: "background-color 120ms",
    "&:active": { background: s.isSelected ? T.brand500 : T.brand100 },
  }),
};

const titleStateSelectStyles = {
  control: (b, s) => ({
    ...b,
    background: "var(--mmp-surface)",
    border: `1px solid ${s.isFocused ? T.brand500 : T.border}`,
    borderRadius: 10,
    minHeight: 42,
    minWidth: 140,
    width: "100%",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(3,149,211,0.15)" : "none",
    paddingLeft: 12,
    paddingRight: 4,
    cursor: "pointer",
    transition: "border-color 150ms, box-shadow 150ms",
    "&:hover": { borderColor: s.isFocused ? T.brand500 : T.borderStrong },
  }),
  valueContainer: (b) => ({ ...b, padding: 0 }),
  singleValue: (b) => ({
    ...b,
    fontSize: 14,
    fontWeight: 700,
    color: T.brandInk,
    margin: 0,
  }),
  // Visible (not transparent) now that this select is searchable — the
  // typed query needs to actually show as the user types it.
  input: (b) => ({
    ...b,
    margin: 0,
    padding: 0,
    color: T.brandInk,
    fontSize: 14,
    fontWeight: 700,
  }),
  placeholder: (b) => ({ ...b, color: T.fg4, margin: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b, s) => ({
    ...b,
    color: T.fg3,
    padding: "0 8px 0 4px",
    transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 180ms cubic-bezier(0.2,0.8,0.2,1)",
    svg: { width: 16, height: 16 },
  }),
  menu: (b) => ({
    ...b,
    background: "var(--mmp-surface)",
    borderRadius: 12,
    marginTop: 6,
    padding: 6,
    boxShadow: "0 12px 32px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
    overflow: "hidden",
    zIndex: 1000,
    minWidth: 180,
  }),
  menuPortal: (b) => ({ ...b, zIndex: 9500 }),
  menuList: (b) => ({ ...b, padding: 0, maxHeight: 280 }),
  option: (b, s) => ({
    ...b,
    fontSize: 13,
    fontWeight: s.isSelected ? 700 : 500,
    borderRadius: 8,
    padding: "9px 12px",
    margin: "2px 0",
    cursor: "pointer",
    color: s.isSelected ? "#fff" : T.brandInk,
    background: s.isSelected
      ? T.brand500
      : s.isFocused
      ? T.brand50
      : "transparent",
    transition: "background-color 120ms",
    "&:active": { background: s.isSelected ? T.brand500 : T.brand100 },
  }),
};

const stackedSelectStyles = {
  // The default react-select container is `position: relative`, which makes
  // the menu (position: absolute) anchor to the Select. We force it static
  // so the menu instead anchors to `.stacked-control` (its grandparent,
  // already position: relative). That way the popover spans the FULL
  // visible trigger box, even when a unit suffix like "hrs" is rendered as
  // a sibling of the Select inside `.stacked-control`.
  container: (b) => ({ ...b, position: "static" }),
  control: (b, s) => ({
    ...b,
    background: "transparent",
    border: 0,
    minHeight: 0,
    boxShadow: "none",
    padding: 0,
    cursor: "pointer",
    "&:hover": { borderColor: "transparent" },
  }),
  valueContainer: (b) => ({ ...b, padding: 0 }),
  singleValue: (b) => ({
    ...b,
    fontSize: 18,
    fontWeight: 700,
    color: T.brandInk,
    margin: 0,
  }),
  input: (b) => ({ ...b, margin: 0, padding: 0, color: "transparent" }),
  placeholder: (b) => ({ ...b, color: T.fg4, margin: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b, s) => ({
    ...b,
    color: T.fg3,
    padding: 0,
    transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 180ms cubic-bezier(0.2,0.8,0.2,1)",
    svg: { width: 18, height: 18 },
  }),
  // Menu + option styling kept in lockstep with the Paycheck Calculator
  // dropdowns (see paycheck.js `selectStyles`) so both pages feel identical.
  menu: (b) => ({
    ...b,
    background: "var(--mmp-surface)",
    borderRadius: 10,
    padding: 4,
    boxShadow: "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
    zIndex: 1000,
    // Anchored to `.stacked-control` (the visible trigger box) via
    // `container: { position: 'static' }`. This makes the popover span
    // exactly the trigger's width regardless of internal unit suffixes.
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    width: "auto",
  }),
  menuPortal: (b) => ({ ...b, zIndex: 9500 }),
  menuList: (b) => ({ ...b, padding: 0, maxHeight: 240 }),
  option: (b, s) => ({
    ...b,
    fontSize: 13,
    fontWeight: s.isSelected ? 700 : 500,
    borderRadius: 6,
    padding: "7px 10px",
    margin: 0,
    cursor: "pointer",
    color: s.isSelected ? "#fff" : T.brandInk,
    background: s.isSelected
      ? T.brand500
      : s.isFocused
      ? T.brand50
      : "transparent",
  }),
};

const FieldControl = (props) => (
  <RsComponents.Control {...props}>
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        color: T.fg2,
        flexShrink: 0,
        marginRight: 12,
        whiteSpace: "nowrap",
      }}
    >
      {props.selectProps.fieldLabel}
    </span>
    {props.children}
  </RsComponents.Control>
);

/* ── Presentational components ──────────────────────────────── */

function LockBadge({ size }) {
  const cls = `lock-badge${size === "sm" ? " sm" : size === "xs" ? " xs" : ""}`;
  return (
    <div className={cls}>
      <Icon.Lock />
    </div>
  );
}

function FieldOpen({ label, value, options, onChange }) {
  const opts = useMemo(
    () => options.map((o) => ({ value: o, label: String(o) })),
    [options],
  );
  return (
    <Select
      classNamePrefix="mmp-field-sel"
      options={opts}
      value={opts.find((o) => o.value === value) || null}
      onChange={(o) => o && onChange(o.value)}
      isSearchable={false}
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      components={{ Control: FieldControl }}
      styles={selectStyles}
      fieldLabel={label}
      aria-label={label}
    />
  );
}

function FieldStacked({ fieldKey, label, unit, value, options, onChange }) {
  const opts = useMemo(
    () => options.map((o) => ({ value: o, label: String(o) })),
    [options],
  );
  return (
    <div
      className={`stacked-field${
        fieldKey ? ` stacked-field--${fieldKey}` : ""
      }`}
      data-tour={fieldKey ? `salary-field-${fieldKey}` : undefined}
    >
      <div className="stacked-label">{label}</div>
      <div className="stacked-control">
        <Select
          classNamePrefix="mmp-stacked-sel"
          options={opts}
          value={opts.find((o) => o.value === value) || null}
          onChange={(o) => o && onChange(o.value)}
          isSearchable={false}
          menuPlacement="auto"
          // No portal here: when portalled, react-select sizes the menu
          // from the trigger's getBoundingClientRect which mis-measures the
          // stacked control and produces a narrower-than-trigger popover.
          // Keeping the menu in-flow lets its width follow the wrapper.
          styles={stackedSelectStyles}
          aria-label={label}
        />
        {unit && <span className="stacked-unit">{unit}</span>}
      </div>
    </div>
  );
}

function FieldLocked({ label, value, onTap }) {
  return (
    <div
      className="locked-field"
      onClick={onTap}
      role="button"
      tabIndex={0}
      onKeyDown={onActivate(onTap)}
    >
      <div className="field">
        <span className="field-label">{label}</span>
        <span className="field-value">{value}</span>
      </div>
      <LockBadge />
    </div>
  );
}

function RankRow({
  rank,
  name,
  city,
  amount,
  maxAmount,
  locked,
  rateSuffix,
  onTap,
}) {
  const width = locked ? 72 : Math.max(8, (amount / maxAmount) * 100);
  return (
    <div
      className="rank-row"
      onClick={onTap}
      role="button"
      tabIndex={0}
      onKeyDown={onActivate(onTap)}
    >
      <div className="rank-row-top">
        <div className={`rank-num ${rank === 1 ? "is-first" : ""}`}>{rank}</div>
        <div className="rank-info">
          <div className="hospital-name">{name}</div>
          {city && <div className="hospital-city">{city}</div>}
        </div>
        {locked ? (
          <div className="rank-amount-locked">
            <span className="ph">{LOCKED_AMOUNT}</span>
            <LockBadge size="sm" />
          </div>
        ) : (
          <span className="rank-amount">
            {formatCurrency(amount)}
            {rateSuffix && (
              <span className="rank-amount-suffix">{rateSuffix}</span>
            )}
          </span>
        )}
      </div>
      <div className="rank-bar">
        <div
          className={`rank-bar-fill ${locked ? "is-locked" : ""}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

// Collapsed/expandable card for hospitals sharing an identical rate
// (grouping decided server-side — see buildRankedEntries; never shown to
// free users — see the isProUser branch there). Header tap toggles expand;
// each expanded member is independently tappable and goes through the same
// lock/paywall path as a normal row (handleRankTap). Members beyond
// GROUP_MEMBERS_VISIBLE_CAP collapse behind an "…and N more" row that
// reveals the rest in place.
function GroupRankRow({
  rank,
  group,
  maxAmount,
  locked,
  rateSuffix,
  expanded,
  onToggle,
  onMemberTap,
  showAllMembers,
  onToggleOverflow,
}) {
  const width = locked ? 72 : Math.max(8, (group.amount / maxAmount) * 100);
  const visibleMembers = showAllMembers
    ? group.members
    : group.members.slice(0, GROUP_MEMBERS_VISIBLE_CAP);
  const hiddenCount = group.members.length - visibleMembers.length;
  return (
    <div className={`rank-row group-row${expanded ? " is-expanded" : ""}`}>
      <div
        className="rank-row-top"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={onActivate(onToggle)}
      >
        <div className={`rank-num ${rank === 1 ? "is-first" : ""}`}>{rank}</div>
        <div className="rank-info">
          <div className="hospital-name">{group.name}</div>
          <div className="group-meta">
            <span className="group-tag">
              <Icon.Grid aria-hidden="true" />
              Same-pay group
            </span>
            <div className="hospital-city">
              {group.tag} · {group.size} hospitals
            </div>
          </div>
        </div>
        {locked ? (
          <div className="rank-amount-locked">
            <span className="ph">{LOCKED_AMOUNT}</span>
            <LockBadge size="sm" />
          </div>
        ) : (
          <span className="rank-amount">
            {formatCurrency(group.amount)}
            {rateSuffix && (
              <span className="rank-amount-suffix">{rateSuffix}</span>
            )}
          </span>
        )}
        <Icon.Chevron className="group-chevron" aria-hidden="true" />
      </div>
      <div className="rank-bar">
        <div
          className={`rank-bar-fill ${locked ? "is-locked" : ""}`}
          style={{ width: `${width}%` }}
        />
      </div>
      {expanded && (
        <div className="group-members">
          {visibleMembers.map((m, i) => (
            <div
              key={m.id ?? `${m.hospital}-${i}`}
              className="group-member-row"
              onClick={(e) => {
                e.stopPropagation();
                onMemberTap(m);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={onActivate(() => onMemberTap(m))}
            >
              <span className="member-name">{m.hospital}</span>
              <span className="member-amount">
                {formatCurrency(m.grossPay)}
                {rateSuffix && (
                  <span className="member-amount-suffix">{rateSuffix}</span>
                )}
              </span>
              <Icon.ChevronRight className="member-chevron" aria-hidden="true" />
            </div>
          ))}
          {hiddenCount > 0 && (
            <div
              className="group-member-more"
              onClick={(e) => {
                e.stopPropagation();
                onToggleOverflow();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={onActivate(onToggleOverflow)}
            >
              …and {hiddenCount} more
              <Icon.ChevronRight aria-hidden="true" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, suffix, locked, onLockedTap }) {
  return (
    <div className="summary-stat">
      <div className="summary-label">{label}</div>
      {locked ? (
        <div
          className="stat-value-locked"
          onClick={onLockedTap}
          role="button"
          tabIndex={0}
          onKeyDown={onActivate(onLockedTap)}
        >
          <span className="ph">{LOCKED_SUMMARY}</span>
          <LockBadge size="sm" />
        </div>
      ) : (
        <div className="stat-value">
          {value}
          {suffix && <span className="stat-value-suffix">{suffix}</span>}
        </div>
      )}
    </div>
  );
}

function PaywallSheet({
  open,
  animateIn,
  onClose,
  headline,
  missing,
  onUpgrade,
  hasUsedProTrial,
}) {
  const sheetRef = useSwipeDismiss(open, onClose);
  if (!open) return null;
  return (
    <div className="mmp-sheet-root">
      <div
        className={`sheet-overlay ${animateIn ? "is-open" : ""}`}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        className={`sheet paywall ${animateIn ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="sheet-drag" aria-hidden="true" />
        <div className="icon-tile">
          <Icon.Lock />
        </div>
        <h2 className="headline">{headline}</h2>
        <p className="subtext">Upgrade to keep going.</p>
        {missing && (
          <div className="missing-box">
            <Icon.Plus />
            <p>{missing}</p>
          </div>
        )}
        <button type="button" className="cta-primary" onClick={onUpgrade}>
          <span className="cta-headline">
            {hasUsedProTrial ? "Get Pro" : "Start 3-day free trial"}
          </span>
          <span className="cta-sub">
            {hasUsedProTrial
              ? "$2.99/month, billed annually at $29.99/year. Cancel anytime."
              : "No charge today. $29.99/year after your 3-day trial."}
          </span>
        </button>
        <button type="button" className="cta-secondary" onClick={onClose}>
          Maybe later
        </button>
      </div>
    </div>
  );
}

function TaxPopup({
  hospital,
  animateIn,
  onClose,
  onUpgrade,
  show401k,
  isProUser,
  ratePer,
}) {
  const sheetRef = useSwipeDismiss(!!hospital, onClose);
  if (!hospital) return null;
  const k401 = hospital.k401 || 0;
  const stateCode = hospital.stateCode || "";
  const rawRows = [
    { label: "Federal", key: "federalTax" },
    { label: `State${stateCode ? ` (${stateCode})` : ""}`, key: "stateTax" },
    ...(hospital.localTax > 0 ? [{ label: "NYC Local", key: "localTax" }] : []),
    { label: "Social Security", key: "ssTax" },
    { label: "Medicare", key: "medicareTax" },
    ...(hospital.sdiTax > 0 ? [{ label: "SDI", key: "sdiTax" }] : []),
  ];
  const total =
    rawRows.reduce((sum, d) => sum + (hospital[d.key] || 0), 0) + k401;
  const dynamicRows = rawRows.filter((d) => Number(hospital[d.key]) > 0);
  const rows =
    show401k && k401 > 0
      ? [...dynamicRows, { label: "401k", key: "k401" }]
      : dynamicRows;
  const grossLabel = `Gross ${RATE_PER_LABEL[ratePer] || ""}`.trim();

  return (
    <div className="mmp-sheet-root">
      <div
        className={`sheet-overlay ${animateIn ? "is-open" : ""}`}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        className={`sheet tax-popup ${animateIn ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="sheet-drag" aria-hidden="true" />
        <div className="tax-head">
          <div className="tax-head-text">
            <div className="rank-label">Rank #{hospital.rank}</div>
            <div className="name">{hospital.name}</div>
            {hospital.city && <div className="city">{hospital.city}</div>}
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon.Close />
          </button>
        </div>

        <div className="open-line">
          <span className="k">{grossLabel}</span>
          <span className="v">{formatCurrency(hospital.gross)}</span>
        </div>
        <div className="open-line open-line--net">
          <span className="k">Net Pay</span>
          <span className="v">{formatCurrency(hospital.net)}</span>
        </div>

        <div
          className={`deductions-box ${
            isProUser ? "deductions-box--open" : ""
          }`}
        >
          <div className="deductions-head">
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              Deductions
              <InfoTooltip text="Federal, state, Social Security, Medicare, and SDI taxes withheld from gross pay. Full breakdown is a Pro feature." />
            </span>
            {!isProUser && <LockBadge size="xs" />}
          </div>
          <div className="deductions-list" aria-hidden={!isProUser}>
            {rows.map((d) => (
              <div className="ded-row" key={d.key}>
                <span className="k">{d.label}</span>
                <span className="v">
                  {isProUser ? `-${formatCurrency(hospital[d.key])}` : "$•••"}
                </span>
              </div>
            ))}
            {isProUser && (
              <div className="ded-row ded-row--total">
                <span className="k">Total Deductions</span>
                <span className="v">-{formatCurrency(total)}</span>
              </div>
            )}
          </div>
        </div>

        {!isProUser && (
          <button type="button" className="cta-upgrade" onClick={onUpgrade}>
            <Icon.Lock />
            <span>Upgrade to see tax breakdown</span>
          </button>
        )}
      </div>
    </div>
  );
}

function TopHospitalSkeleton() {
  return (
    <div className="top-hospital-card top-hospital-card--skeleton" aria-hidden="true">
      <div className="sk sk--label" />
      <div className="sk sk--amount" />
      <div className="sk sk--meta" />
      <div className="sk sk--pill-sm" />
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────── */

const Salaries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((s) => s.auth?.user);
  const profileStore = useSelector((s) => s.profile);
  const salariesSlice = useSelector((s) => s.salaries);

  // "Complete your profile" nudge shown above the title, in its own card —
  // picks the single highest-priority missing field rather than a generic
  // percentage, since that's more actionable here. Dismissal is in-memory
  // only (not persisted) so the nudge reappears on reload, re-login, or a
  // fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const salariesProfileData = profileStore?.profileItem?.data;
  const profileCompletionPercent = getProfileCompletionPercent(
    salariesProfileData,
  );
  const activeProfileNudge = salariesProfileData
    ? SALARIES_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(salariesProfileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  const hasUsedProTrial =
    user?.has_used_pro_trial ??
    profileStore?.profileItem?.data?.has_used_pro_trial ??
    false;
  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser, isFree } = usePlan();

  // Free-tier daily input-change cap (1/day), mirroring the paycheck calculator.
  // usageInfo comes from the backend (mount fetch + each counted change echo);
  // countNextFetchRef flags the next /filter request as a real input change so
  // page loads & pagination stay uncounted.
  const [usageInfo, setUsageInfo] = useState(null);
  const countNextFetchRef = useRef(false);
  const limitReached = isFree && Boolean(usageInfo?.limit_reached);

  const [filters, setFilters] = useState(() => loadInitialFilters(isProUser));
  const [stateOptions, setStateOptions] = useState([]);
  const stateName = useMemo(() => {
    if (!filters.state) return "All States";
    const match = stateOptions.find((s) => s.code === filters.state);
    return match?.name || filters.state;
  }, [filters.state, stateOptions]);
  const [salariesData, setSalariesData] = useState([]);
  const [totalHospitals, setTotalHospitals] = useState(0);
  // Keyed by the backend's group id, so an expanded group stays expanded as
  // more pages stream in via infinite scroll — only a fresh search (new
  // filters) clears it.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  // Separate from expandedGroups: tracks which expanded groups have had
  // their "…and N more" overflow row tapped to reveal every member.
  const [expandedOverflowGroups, setExpandedOverflowGroups] = useState(
    () => new Set(),
  );
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [taxInputState, setTaxInputState] = useState({});

  const [paywall, setPaywall] = useState(null);
  const [taxFor, setTaxFor] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);

  // Scroll-triggered upgrade popup — mirrors the Jobs page pattern.
  // Shown once per session for non-pro users when they pass ~35% of the page.
  const [showScrollPopup, setShowScrollPopup] = useState(false);
  const [hasClosedPopup, setHasClosedPopup] = useState(false);

  /* One helper for every analytics event. No-ops without a session id. */
  const track = (event_type, extra) => {
    const session_id = sessionStorage.getItem("comparison_session_id");
    if (!session_id) return;
    dispatch(
      calculatorAnalytics({
        calculator_type: "hospital_salary",
        event_type,
        session_id,
        timestamp: new Date().toISOString(),
        ...extra,
      }),
    );
    if (window.fbq) window.fbq("trackCustom", "UsedCalculator");
  };

  /* Sheets */
  const openPaywall = (headline, missing, source = "salary_page") =>
    setPaywall({ headline, missing, source });

  const closeSheet = () => {
    setAnimateIn(false);
    setTimeout(() => {
      setPaywall(null);
      setTaxFor(null);
    }, 260);
  };

  /* Fetch distinct states once on mount */
  useEffect(() => {
    dispatch(getHospitalStates())
      .then((res) => {
        const data = res?.payload?.data;
        if (Array.isArray(data)) setStateOptions(data);
      })
      .catch(() => {});
  }, [dispatch]);

  const handleStateChange = (value) => {
    if (limitReached) {
      track("calculator_limit_hit", { input_name: "state", results_viewed: true });
      openPaywall(
        "You've used your free salary update for today",
        "Upgrade to Pro to adjust your salary inputs as often as you like.",
        "salary_input_limit",
      );
      return;
    }
    track("input_adjusted", { input_name: "state", inputs: value, results_viewed: true });
    if (isFree) countNextFetchRef.current = true;
    setFilters((p) => ({ ...p, state: value, page: 1 }));
  };

  /* Handlers */
  const handleFieldChange = (field, value) => {
    // Free users get one input change per day (mirrors the paycheck calculator).
    // Once the cap is hit, changing a field opens the paywall instead of applying.
    if (limitReached) {
      track("calculator_limit_hit", {
        input_name: field,
        results_viewed: true,
      });
      openPaywall(
        "You've used your free salary update for today",
        "Upgrade to Pro to adjust your salary inputs as often as you like.",
        "salary_input_limit",
      );
      return;
    }
    track("input_adjusted", { input_name: field, results_viewed: true });
    // Flag this fetch as a real input change so the backend counts it (page
    // loads & infinite-scroll pagination don't set this, so they stay free).
    if (isFree) countNextFetchRef.current = true;
    setFilters((p) => ({ ...p, [field]: value, page: 1 }));
  };

  const handleProInputTap = (input) => {
    track("calculator_limit_hit", {
      input_name: input.key,
      results_viewed: true,
    });
    openPaywall(
      input.paywall,
      "Adjust shift differentials, overtime, and double time with Pro",
      "salary_input_locked",
    );
  };

  const handleRankTap = (idx, result) => {
    // Free users can't drill into the top hospital — clicking it is a no-op.
    if (!isProUser && idx === 0) return;
    if (!isProUser && idx > 0) {
      const lockedCount = Math.max(salariesData.length - 1, 0);
      const countLabel = lockedCount > 0 ? `${lockedCount}+ ` : "";
      openPaywall(
        // "Unlock all union hospital salaries",
        "Unlock all Bay Area union hospital salaries",
        // `Unlock all ${countLabel}California union hospital salaries`,
        `Unlock all 10+ Bay Area hospital salaries`,
        "salary_rank_locked",
      );
      return;
    }
    setTaxFor({
      rank: idx + 1,
      name: result.hospital,
      city: result.city || result.location || "",
      gross: result.taxes?.gross,
      net: result.taxes?.net,
      federalTax: result.taxes?.federalTax,
      stateTax: result.taxes?.stateTax,
      localTax: result.taxes?.localTax || 0,
      ssTax: result.taxes?.ssTax,
      medicareTax: result.taxes?.medicareTax,
      sdiTax: result.taxes?.sdiTax,
      stateCode: result.taxes?.stateCode || result.state || "",
      k401: result.taxes?.deductionsMonthly?.["401k"] || 0,
    });
  };

  const handleSummaryLockedTap = () =>
    openPaywall(
      `See your real net pay at ${topHospital?.hospital || "top hospital"}`,
      "Unlock federal, state, FICA, and SDI breakdown for every hospital",
      "salary_summary_locked",
    );

  const handleUpgrade = () => {
    track("upgrade_clicked");
    closeSheet();
    navigate("/profile/subscription", {
      state: { source: paywall?.source || "salary_page" },
    });
  };

  /* Sheet slide-in: one frame after mount, flip is-open on. */
  useEffect(() => {
    if (!paywall && !taxFor) {
      setAnimateIn(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(id);
  }, [paywall, taxFor]);

  /* Lock body scroll while a sheet is open so the page underneath doesn't
   * scroll away during the swipe-down-to-dismiss gesture on mobile. Uses
   * the position:fixed trick — overflow:hidden alone doesn't stop iOS.
   *
   * Runs as a layout effect so the unlock + scrollTo happen synchronously
   * before the next paint. With plain useEffect the browser would paint
   * once after unlock (page at y=0) and again after scrollTo, giving a
   * visible "jump to top, then snap back" flicker on mobile when the
   * modal closes. Smooth-scroll on <html> is also disabled momentarily
   * so the scroll restore is instant regardless of global CSS. */
  useIsoLayoutEffect(() => {
    const isOpen = !!paywall || !!taxFor;
    if (!isOpen) return undefined;
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      const prevScrollBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
      html.style.scrollBehavior = prevScrollBehavior;
    };
  }, [paywall, taxFor]);

  /* Activity lifecycle */
  usePageTracking("SALARIES");

  useEffect(() => {
    dispatch(activityStart({ page_name: "SALARIES", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "SALARIES" }));
  }, [dispatch]);

  /* Tax tab writes deductions into Redux; mirror them into local state. */
  useEffect(() => {
    if (salariesSlice?.taxInputData)
      setTaxInputState(salariesSlice.taxInputData);
  }, [salariesSlice]);

  useEffect(() => {
    setFilters((p) => ({
      ...p,
      deductions: taxInputState?.deductions || {},
      rate_per: taxInputState?.rate_per || p.rate_per,
    }));
  }, [taxInputState]);

  /* Mirror filters back to Redux (Tax tab reads them). */
  useEffect(() => {
    dispatch(salariesDataAction({ ...filters, stateName }));
  }, [filters, stateName, dispatch]);

  /* Fetch today's input-change usage on mount so a returning free user who
   * already spent their daily change lands with inputs locked. (Each counted
   * change also echoes a fresh usage block — see the fetch effect below.) */
  useEffect(() => {
    if (!isFree) return undefined;
    let cancelled = false;
    salariesService
      .usage()
      .then((res) => {
        if (!cancelled) setUsageInfo(res?.data?.data || null);
      })
      .catch(() => {
        /* non-auth or transient — leave inputs unlocked */
      });
    return () => {
      cancelled = true;
    };
  }, [isFree]);

  /* Fetch salaries on filter change. */
  useEffect(() => {
    let cancelled = false;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    localStorage.setItem(
      "filters",
      JSON.stringify({
        ...filters,
        page: 1,
        deductions: taxInputState?.deductions,
      }),
    );
    // Count this request only if it was triggered by a real input change
    // (handleFieldChange set the flag). Consume the flag immediately so the
    // next load/pagination fetch doesn't inherit it.
    const counted = countNextFetchRef.current;
    countNextFetchRef.current = false;
    const payload = counted
      ? { ...filters, count_input_change: true }
      : filters;
    dispatch(getSalaries(payload))
      .then((res) => {
        if (cancelled) return;
        // Daily cap hit — sync usage (locks inputs), show paywall, keep old data.
        if (
          res?.payload?.errorType === "HOSPITAL_SALARY_FILTER_LIMIT_REACHED"
        ) {
          setUsageInfo(res.payload.data || null);
          openPaywall(
            "You've used your free salary update for today",
            "Upgrade to Pro to adjust your salary inputs as often as you like.",
            "salary_input_limit",
          );
          return;
        }
        const data = res?.payload?.data;
        if (data?.usage) setUsageInfo(data.usage);
        const docs = data?.docs || [];
        const totalPages = data?.pages || 1;
        const total = data?.summary?.total_hospitals ?? data?.total ?? 0;
        if (filters.page === 1) {
          setExpandedGroups(new Set());
          setExpandedOverflowGroups(new Set());
        }
        setSalariesData((prev) =>
          filters.page === 1 ? docs : [...prev, ...docs],
        );
        setTotalHospitals(total);
        setHasMore(filters.page < totalPages);
      })
      .catch((e) => console.error("Salaries fetch error:", e))
      .finally(() => {
        if (!cancelled) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters, taxInputState?.deductions, dispatch]);

  /* Infinite scroll */
  useEffect(() => {
    const onScroll = () => {
      if (!hasMore || loadingMoreRef.current) return;
      // Pause pagination while the guided tour drives the scroll — otherwise
      // scrolling to the last step keeps triggering "load more", growing the
      // page under the tour so the final section never settles.
      if (document.body.classList.contains("mmp-tour-active")) return;
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 100
      ) {
        loadingMoreRef.current = true;
        setFilters((p) => ({ ...p, page: p.page + 1 }));
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore]);

  /* Session id provisioning */
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("comparison_session_id");
      sessionStorage.removeItem("session_expiry");
      return;
    }
    if (!user.id) return;
    const existing = sessionStorage.getItem("comparison_session_id");
    const expiry = sessionStorage.getItem("session_expiry");
    if (existing && expiry && Date.now() < Number(expiry)) return;
    dispatch(sessionId()).then((res) => {
      const data = res.payload?.data;
      if (!data?.session_id) return;
      sessionStorage.setItem("comparison_session_id", data.session_id);
      sessionStorage.setItem(
        "session_expiry",
        String(Date.now() + data.expires_in * 1000),
      );
    });
  }, [user, dispatch]);

  /* Page view */
  useEffect(() => {
    track("hospital_salary_calculator_view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  /* Scroll popup effect — same trigger rules as Jobs.js. */
  useEffect(() => {
    const handleScrollPopup = () => {
      if (hasClosedPopup) return;
      if (isProUser) return;
      // Don't pop the upgrade sheet while the guided tour is driving scroll.
      if (document.body.classList.contains("mmp-tour-active")) return;

      const scrollPosition = window.scrollY;
      const pageHeight = document.body.scrollHeight - window.innerHeight;
      if (pageHeight <= 0) return;

      if (scrollPosition / pageHeight > 0.35 && !showScrollPopup) {
        setShowScrollPopup(true);
      }
    };

    window.addEventListener("scroll", handleScrollPopup);
    return () => window.removeEventListener("scroll", handleScrollPopup);
  }, [showScrollPopup, hasClosedPopup, isProUser]);

  /* Derived */
  const stateSelectOpts = useMemo(
    () => [
      { value: "", label: "All States" },
      ...stateOptions.map((s) => ({ value: s.code, label: s.name || s.code })),
    ],
    [stateOptions],
  );

  // Each doc from the backend is already a finished ranking slot — a plain
  // hospital, or a same-pay group with its full member list embedded. See
  // buildRankedEntries for the shape. Free users never get group entries —
  // groups are unpacked back into individual rows (grouping is Pro-only).
  const rankedEntries = useMemo(
    () => buildRankedEntries(salariesData, isProUser),
    [salariesData, isProUser],
  );

  const maxSalary = useMemo(() => {
    const amounts = rankedEntries.map((e) =>
      e.type === "group" ? e.amount || 0 : e.hospital?.grossPay || 0,
    );
    return amounts.length ? Math.max(...amounts) : 1;
  }, [rankedEntries]);

  // Top-of-list summary card/stats. If the #1 slot is itself a group, show
  // the group's name (e.g. "NYC Health + Hospitals") — every member shares
  // the same grossPay/taxes by definition, so any member's numbers work.
  const topEntry = rankedEntries[0] || null;
  const topHospital = !topEntry
    ? null
    : topEntry.type === "group"
      ? {
          ...topEntry.members[0],
          hospital: topEntry.name,
          city: `${topEntry.size} hospitals`,
        }
      : topEntry.hospital;

  const show401k = !!taxInputState?.deductions?.["401k"];

  const toggleGroup = (key) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleOverflow = (key) =>
    setExpandedOverflowGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <>
      <NoIndexSEO title={seoConfig.salaries.title} />
      <div className="mmp-locked-salary">
        <div className="shell">
          {activeProfileNudge && !profileNudgeDismissed && (
            <div
              className="salaries-profile-nudge"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/profile")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/profile");
                }
              }}
            >
              <div className="salaries-profile-nudge-icon">
                <UserRound />
              </div>
              <div className="salaries-profile-nudge-body">
                <div className="salaries-profile-nudge-head">
                  <span className="salaries-profile-nudge-eyebrow">
                    Complete your profile
                  </span>
                  <span className="salaries-profile-nudge-pct">
                    {profileCompletionPercent}%
                  </span>
                </div>
                <div className="salaries-profile-nudge-bar-track">
                  <div
                    className="salaries-profile-nudge-bar-fill"
                    style={{ width: `${profileCompletionPercent}%` }}
                  />
                </div>
                <span className="salaries-profile-nudge-text">
                  {activeProfileNudge.message}
                </span>
              </div>
              <button
                type="button"
                className="salaries-profile-nudge-close"
                aria-label="Dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissProfileNudge();
                }}
              >
                ×
              </button>
            </div>
          )}
          <div className="page-card">
          <div className="title-bar" data-tour="salary-titlebar">
            <div className="title-bar__title">
              Hospital Salary Calculator
              <InfoTooltip text="Estimated pay is based on real hospital union contracts and adjusted using the filters you select below." />
            </div>
            {stateSelectOpts.length > 1 && (
              <div className="title-bar__state" data-tour="salary-state">
                <Select
                  classNamePrefix="mmp-title-state-sel"
                  options={stateSelectOpts}
                  value={
                    stateSelectOpts.find(
                      (o) => o.value === (filters.state || ""),
                    ) || stateSelectOpts[0]
                  }
                  onChange={(o) => handleStateChange(o?.value ?? "")}
                  isSearchable
                  placeholder="Search state…"
                  menuPlacement="auto"
                  styles={titleStateSelectStyles}
                  aria-label="State"
                />
              </div>
            )}
            <div className="title-bar__subtitle">
              Real take-home from {stateName} hospital union contracts.
            </div>
          </div>

          {!isProUser && (
            <div className="free-plan-banner" role="status">
              <span className="dot" aria-hidden="true" />
              <span className="text">Free plan: viewing top hospital only</span>
              <button
                type="button"
                className="upgrade-link"
                // onClick={() =>
                //   openPaywall(
                //     "Unlock all Bay Area union hospital salaries",
                //     "Unlock all 10+ Bay Area hospital salaries",
                //     "salary_free_plan_banner",
                //   )
                // }
                onClick={() => navigate("/profile/subscription")}
              >
                Upgrade <span aria-hidden="true">→</span>
              </button>
            </div>
          )}

          {loadingMore && !topHospital ? (
            <TopHospitalSkeleton />
          ) : topHospital ? (
            <div className="top-hospital-card" data-tour="salary-hero">
              <div className="th-label">
                Top Hospital
                <InfoTooltip text="Estimate your monthly hospital pay based on your selected state, role, hours, experience, and other filters." />
                <span className="th-sep">·</span>
                <span className="th-hospital">{topHospital.hospital}</span>
              </div>
              <div className="th-amount">
                {formatCurrency(topHospital.grossPay)}
              </div>
              <div className="th-meta">
                estimated {RATE_PER_LABEL[filters.rate_per] || "monthly"}
                <span className="th-sep">·</span>
                before housing
              </div>
              <div className="th-pill">
                Top of {totalHospitals || salariesData.length || 0} hospitals
              </div>
            </div>
          ) : null}

          <div className="layout">
            <aside className="col-filters">
              <div className="customization-card" data-tour="salary-inputs">
                <div className="your-inputs-title">
                  Your inputs
                  <InfoTooltip text="These inputs are used to personalize your salary estimates and hospital rankings." />
                </div>
                {limitReached && (
                  <button
                    type="button"
                    className="inputs-limit-note"
                    onClick={() =>
                      openPaywall(
                        "You've used your free salary update for today",
                        "Upgrade to Pro to adjust your salary inputs as often as you like.",
                        "salary_input_limit",
                      )
                    }
                  >
                    <Icon.Lock />
                    <span>
                      Daily free update used · upgrade to change again
                    </span>
                  </button>
                )}
                <div className="field-group field-group--stacked">
                  {OPEN_INPUTS.map(({ key, label, unit }) => (
                    <FieldStacked
                      key={key}
                      fieldKey={key}
                      label={label}
                      unit={unit}
                      value={filters[key]}
                      options={DROPDOWN_OPTIONS[key]}
                      onChange={(v) => handleFieldChange(key, v)}
                    />
                  ))}
                </div>

{!isProUser && (
                  <div className="pro-inputs-header">
                    <Icon.Lock />
                    <span>Pro inputs · shift differentials &amp; overtime</span>
                  </div>
                )}
                <div className="field-group">
                  {PRO_INPUTS.map((input) => (
                    <div key={input.key} data-tour={`salary-field-${input.key}`}>
                      {isProUser ? (
                        <FieldOpen
                          label={input.label}
                          value={filters[input.key]}
                          options={DROPDOWN_OPTIONS[input.key]}
                          onChange={(v) => handleFieldChange(input.key, v)}
                        />
                      ) : (
                        <FieldLocked
                          label={input.label}
                          value={input.lockedValue}
                          onTap={() => handleProInputTap(input)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </aside>

            <main className="col-content">
              <div className="rankings-card" data-tour="salary-rankings">
                <div className="rankings-header">
                  <div className="rankings-title">
                    Hospital salary rankings
                    <InfoTooltip text="Hospitals are ranked by estimated gross pay based on your selected filters." />
                  </div>
                  <div className="rankings-count">
                    {totalHospitals || salariesData.length || 0} hospitals
                  </div>
                </div>
                {salariesData.length === 0 ? (
                  <div className="state-msg">
                    {loadingMore
                      ? "Loading…"
                      : "No data available for selected criteria"}
                  </div>
                ) : (
                  rankedEntries.map((entry, idx) => {
                    const locked = !isProUser && idx > 0;
                    if (entry.type === "group") {
                      return (
                        <GroupRankRow
                          key={entry.key}
                          rank={idx + 1}
                          group={entry}
                          maxAmount={maxSalary}
                          locked={locked}
                          rateSuffix={RATE_PER_SHORT[filters.rate_per]}
                          expanded={expandedGroups.has(entry.key)}
                          onToggle={() => toggleGroup(entry.key)}
                          onMemberTap={(member) => handleRankTap(idx, member)}
                          showAllMembers={expandedOverflowGroups.has(entry.key)}
                          onToggleOverflow={() => toggleOverflow(entry.key)}
                        />
                      );
                    }
                    const r = entry.hospital;
                    return (
                      <RankRow
                        key={entry.key}
                        rank={idx + 1}
                        name={r.hospital}
                        city={r.city || r.location || ""}
                        amount={r.grossPay}
                        maxAmount={maxSalary}
                        locked={locked}
                        rateSuffix={RATE_PER_SHORT[filters.rate_per]}
                        onTap={() => handleRankTap(idx, r)}
                      />
                    );
                  })
                )}
                {loadingMore && salariesData.length > 0 && (
                  <div className="state-msg">Loading more…</div>
                )}
              </div>
            </main>
          </div>

          {/* <div className="section-label">Salary Summary</div> */}
          <div className="summary-card" data-tour="salary-summary">
            <div className="top-hospital">
              <div className="summary-label">Top Hospital</div>
              <div className="summary-value">
                {topHospital?.hospital ?? "—"}
              </div>
            </div>
            <div className="summary-grid">
              <SummaryStat
                label="Highest Salary"
                value={topHospital ? formatCurrency(topHospital.grossPay) : "—"}
                suffix={RATE_PER_SHORT[filters.rate_per]}
                locked={false}
                onLockedTap={handleSummaryLockedTap}
              />
              <SummaryStat
                label="Est. Net Pay"
                value={
                  topHospital ? formatCurrency(topHospital.taxes?.net) : "—"
                }
                suffix={RATE_PER_SHORT[filters.rate_per]}
                locked={!isProUser}
                onLockedTap={handleSummaryLockedTap}
              />
              {/* {isProUser && (
                <SummaryStat
                  label="Hourly Rate"
                  value={topHospital ? `${formatCurrency(topHospital.hourlyRate)}/hr` : "—"}
                  locked={false}
                />
              )} */}
            </div>
          </div>
          </div>{/* /page-card */}
        </div>
      </div>

      <PaywallSheet
        open={!!paywall}
        animateIn={animateIn}
        onClose={closeSheet}
        headline={paywall?.headline}
        missing={paywall?.missing}
        onUpgrade={handleUpgrade}
        hasUsedProTrial={hasUsedProTrial}
      />
      <TaxPopup
        hospital={taxFor}
        animateIn={animateIn}
        onClose={closeSheet}
        show401k={show401k}
        isProUser={isProUser}
        ratePer={filters.rate_per}
        onUpgrade={() => {
          closeSheet();
          setTimeout(
            () =>
              openPaywall(
                "See the full tax breakdown with Pro",
                "See the full federal, state, SS, Medicare, and SDI breakdown with Pro",
                "salary_tax_breakdown",
              ),
            280,
          );
        }}
      />

      {showScrollPopup && (
        <div className={`scroll-popup ${showScrollPopup ? "visible" : ""}`}>
          <div className="popup-inner">
            <div className="popup-header">
              <h3 className="popup-title">Upgrade to Pro</h3>
              <button
                className="popup-close"
                onClick={() => {
                  setShowScrollPopup(false);
                  setHasClosedPopup(true);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <p className="popup-subtext">
              Unlock premium salary insights and powerful tools to explore
              better career opportunities.
            </p>

            <div className="popup-pro-section">
              {hasUsedProTrial ? (
                <>
                  <p className="popup-pro-title">Pro Plan</p>
                  <p className="popup-free-text">$29.99/year</p>
                  <p className="popup-no-charge">
                    Salary comparisons, cost of living filters, investment tools
                    &amp; job data.
                  </p>
                  <div className="popup-price-divider" />
                  <p className="popup-pro-title">Pro + Newsletter</p>
                  <p className="popup-yearly-price">$49.99/year</p>
                  <p className="popup-charge-note">
                    Everything in Pro plus the MapMyPay Insider weekly
                    newsletter.
                  </p>
                </>
              ) : (
                <>
                  <p className="popup-pro-title">Start your 3-day Pro trial</p>
                  <p className="popup-free-text">FREE for 3 days</p>
                  <p className="popup-no-charge">No charge today</p>
                  <div className="popup-price-divider" />
                  <p className="popup-yearly-price">$29.99/year</p>
                  <p className="popup-charge-note">
                    charged on day 4 unless canceled
                  </p>
                </>
              )}
            </div>

            <div className="popup-actions">
              <button
                className="refer-btn"
                onClick={() => {
                  setShowScrollPopup(false);
                  setHasClosedPopup(true);
                  navigate("/referral");
                }}
              >
                Refer & Earn Pro
              </button>

              <button
                className="subscribe-btn"
                onClick={() => {
                  track("upgrade_clicked", { source: "salaries_page_scroll" });
                  setShowScrollPopup(false);
                  setHasClosedPopup(true);
                  navigate("/profile/subscription", {
                    state: { source: "salaries_page_scroll" },
                  });
                }}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Salaries;
